import { Box, Card, Chip, Grid, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import pdfLogo from '../../../../assets/images/pdf-icon-4.png';
import React, { useState, useEffect, useContext } from 'react';
import GeneratePdf from './GeneratePdf';
import { UserDataContext } from '../ViewProfile';
import stateList from '../../../../utils/stateList';
import { numberToWords } from '../../../../utils/numberToWords';

const RegistrationFeeSection = ({ registrationFee }) => {
  const { personalDetails, businessDetailsData } = useContext(UserDataContext)
  const [invoiceData, setInvoiceData] = useState(null);
  const [totals, setTotals] = useState({
    totalQuantity: 0,
    totalItems: 0,
    subTotal: 0,
    totalSGST: 0,
    totalCGST: 0,
    totalIGST: 0,
    totalTax: 0,
    grandTotal: 0
  });
  useEffect(() => {
    if (invoiceData && invoiceData?.items?.length > 0) {
      calculateTotals(invoiceData.items);
    }
  }, [invoiceData]);
  const trimAddress = (address) => {
    const parts = address.split(",");
    if (parts.length >= 3) {
      return parts.slice(0, 2).join(",").trim();
    }
    return address.trim();
  };
  useEffect(() => {
    if (registrationFee) {
      setInvoiceData((pre) => ({
        ...pre,
        // Company Information
        company: {
          name: "Cudel Delight Solutions Pvt. Ltd.",
          address: "B-154, Sector-63, Noida, Gautambuddha Nagar,",
          state: "Uttar Pradesh",
          pincode: "201301",
          gstin: "09AAHCC0707E1ZP",
          stateCode: "09",
        },
        // Invoice Details
        invoice: {
          number: `CD/${registrationFee.txnId}/UP/${dayjs(
            registrationFee.txnDate
          ).format("YY")}`,
          date: dayjs(registrationFee.txnDate).format("DD-MMM-YY"),
          reference: registrationFee.txnId,
          mode: registrationFee.paymentMethod,
        },
        // Buyer Information
        buyer: {
          name:
            personalDetails?.firstName ||
            "" + "" + personalDetails?.middleName ||
            "" + "" + personalDetails?.lastName ||
            "",
          address:
            trimAddress(personalDetails?.serviceAddress?.address) || "",
          state: personalDetails?.serviceAddress?.state || "",
          pincode: personalDetails?.serviceAddress?.pincode || "",
          panIt: businessDetailsData?.panNumber || "",
          gstin: businessDetailsData?.gstNumber || "",
          placeOfSupply: personalDetails?.serviceAddress?.state || "",
          stateCode:
            stateList.find(
              (state) => state.name == personalDetails?.serviceAddress?.state
            )?.code || "",
          buyerOrderNo: "PO123456",
          termsOfDelivery: "Immediate",
        },
        // Items/Services
        items: [
          {
            description: "Registration Fees",
            hsnSac: "996331",
            rate: registrationFee.baseAmount,
            quantity: 1,
            amount: registrationFee.totalAmount,
            cgstRate: registrationFee?.igst > 0 ? 0 : 9,
            sgstRate: registrationFee?.igst > 0 ? 0 : 9,
            igstRate: registrationFee?.igst > 0 ? 18 : 0,
            discount: registrationFee.discountPercent,
          },
        ],
        // Totals
        totals: {
          subTotal: registrationFee.baseAmount,
          cgstTotal: registrationFee.cgst,
          sgstTotal: registrationFee.sgst,
          igstTotal: registrationFee.igst,
          grandTotal: registrationFee.totalAmount,
          amountInWords: numberToWords(registrationFee.totalAmount),
        },
        // Tax Details
        taxBreakup: [
          {
            type:
              invoiceData?.company?.state == invoiceData?.buyer?.state
                ? "Intra State"
                : "Inter State",
            rate: 18,
            taxableValue: registrationFee.baseAmount,
            cgstAmount: registrationFee.cgst,
            sgstAmount: registrationFee.sgst,
            igstAmount: registrationFee.igst,
          },
        ],
        // Additional Details
        additional: {
          remarks: `Reference: ${registrationFee.txnId}, DATE-${dayjs(
            registrationFee.txnDate
          ).format("DD-MM-YYYY")}`,
          bankDetails: {
            bankName: "HDFC Bank",
            accountNo: "XXXXXXXXXXXX1234",
            ifscCode: "HDFC0001234",
            branch: "Noida Sector 63",
          },
          termsAndConditions: [
            "1. Payment should be made within 30 days",
            "2. Goods once sold cannot be returned",
            "3. Interest at 18% p.a. will be charged on delayed payments",
          ],
        },
      }));
    }
  }, [registrationFee]);

  const [taxBreakup, setTaxBreakup] = useState({
    slab5: { baseAmount: 0, discount: 0, sgst: 0, cgst: 0, igst: 0, total: 0 },
    slab12: { baseAmount: 0, discount: 0, sgst: 0, cgst: 0, igst: 0, total: 0 },
    slab18: { baseAmount: 0, discount: 0, sgst: 0, cgst: 0, igst: 0, total: 0 },
    slab28: { baseAmount: 0, discount: 0, sgst: 0, cgst: 0, igst: 0, total: 0 }
  });
  const calculateTotals = (items) => {
    const newTaxBreakup = {
      slab5: { baseAmount: 0, discount: 0, sgst: 0, cgst: 0, igst: 0, total: 0 },
      slab12: { baseAmount: 0, discount: 0, sgst: 0, cgst: 0, igst: 0, total: 0 },
      slab18: { baseAmount: 0, discount: 0, sgst: 0, cgst: 0, igst: 0, total: 0 },
      slab28: { baseAmount: 0, discount: 0, sgst: 0, cgst: 0, igst: 0, total: 0 }
    };

    let totalQty = 0;
    let subTotal = 0;
    let totalDiscount = 0;
    let totalSGST = 0;
    let totalCGST = 0;
    let totalIGST = 0;

    items.forEach(item => {
      const totalTaxRate = (item.sgstRate + item.cgstRate + item.igstRate);
      // Handle discount calculation safely
      const discountAmount = item.discount ? (item.rate * item.discount) / 100 : 0;
      const effectiveRate = item.rate - discountAmount;
      const taxedAmount = effectiveRate * (totalTaxRate / 100);
      const taxableAmount = effectiveRate * item.quantity;
      const amount = (effectiveRate + taxedAmount) * item.quantity;
      const totalDiscountAmount = discountAmount * item.quantity;

      totalQty += item.quantity;
      subTotal += item.rate * item.quantity;

      // Determine which slab the item belongs to based on total tax rate
      const slabKey = `slab${totalTaxRate}`;

      if (newTaxBreakup[slabKey]) {
        newTaxBreakup[slabKey].baseAmount += item.rate * item.quantity;
        newTaxBreakup[slabKey].discount += totalDiscountAmount;
        newTaxBreakup[slabKey].sgst += taxableAmount * (item.sgstRate / 100);
        newTaxBreakup[slabKey].cgst += taxableAmount * (item.cgstRate / 100);
        newTaxBreakup[slabKey].igst += taxableAmount * (item.igstRate / 100);
        newTaxBreakup[slabKey].total += (
          newTaxBreakup[slabKey].sgst +
          newTaxBreakup[slabKey].cgst +
          newTaxBreakup[slabKey].igst
        );
      }

      totalDiscount += totalDiscountAmount;
      totalCGST += taxableAmount * (item.cgstRate / 100);
      totalSGST += taxableAmount * (item.sgstRate / 100);
      totalIGST += taxableAmount * (item.igstRate / 100);
    });

    const totalTax = totalSGST + totalCGST + totalIGST;
    const grandTotal = subTotal + totalTax - totalDiscount;

    setTaxBreakup(newTaxBreakup);
    setTotals({
      totalQuantity: totalQty,
      totalItems: items.length,
      subTotal: subTotal,
      totalSGST: totalSGST,
      totalCGST: totalCGST,
      totalIGST: totalIGST,
      totalTax: totalTax,
      grandTotal: grandTotal,
      totalDiscount: totalDiscount
    });
  };
  return (
    <Grid item xs={12}>
      <Card>
        <Box sx={{ p: ".5% 2%", border: "2px solid lightGray", bgcolor: "lightGray", }} >
          <Typography sx={{ fontWeight: "600", fontSize: ".85rem" }}>
            Registration Fee Info
          </Typography>
        </Box>
        <Grid container spacing={1} sx={{ p: "1%" }}>
          <Grid item xs={12} sm={6} md={3} order={{xs:2,md:1}} sx={{}}>
            <Typography sx={{ fontWeight: "600", display: "flex ", alignItems: "center", fontSize: ".75rem", }}>
              Payment Category
            </Typography>
            <TextField
              disabled
              fullWidth
              size="small"
              type="number"
              placeholder="Registration Fee"
              inputProps={{
                style: { fontSize: "0.65rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} order={{xs:3,md:2}} sx={{}}>
            <Typography
              sx={{
                fontWeight: "600",
                display: "flex ",
                alignItems: "center",
                fontSize: ".75rem",
              }}
            >
              Transaction Type
            </Typography>
            <TextField
              fullWidth
              disabled
              size="small"
              type="number"
              placeholder="Credit"
              inputProps={{
                style: { fontSize: "0.65rem" },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6} order={{xs:1,md:3}}>
            <Grid container spacing={1}>
              <Grid
                item
                xs={9}
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <Typography
                  sx={{
                    fontWeight: "600",
                    display: "flex ",
                    alignItems: "center",
                    fontSize: ".75rem",
                  }}
                >
                  Payment Status
                </Typography>
                <Chip
                  label={registrationFee?.status ? "Completed" : "Pending"}
                  size="small"
                  color={registrationFee?.status ? "success" : "warning"}
                  sx={{ fontSize: ".75rem" }}
                />
              </Grid>
              <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                {registrationFee &&
                  <GeneratePdf invoiceData={invoiceData} taxBreakup={taxBreakup} totals={totals} />
                }
              </Grid>
            </Grid>
          </Grid>
          {registrationFee &&
            <>
              <Grid item xs={12} sm={6} md={3} order={{xs:4}} sx={{}}>
                <Typography
                  sx={{
                    fontWeight: "600",
                    display: "flex ",
                    alignItems: "center",
                    fontSize: ".75rem",
                  }}
                >
                  Payment Method
                </Typography>
                <TextField
                  disabled
                  fullWidth
                  size="small"
                  value={registrationFee?.paymentMethod}
                  inputProps={{
                    style: { fontSize: "0.65rem" },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} order={{xs:5}} sx={{}}>
                <Typography
                  sx={{
                    // width: "60%",
                    fontWeight: "600",
                    display: "flex ",
                    alignItems: "center",
                    fontSize: ".75rem",
                  }}
                >
                  Transaction Date<span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  size="small"
                  value={registrationFee?.txnDate ? dayjs(registrationFee?.txnDate).format("DD-MMM-YY") : ""}
                  inputProps={{
                    style: { fontSize: "0.65rem" },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} order={{xs:6}} sx={{}}>
                <Typography
                  sx={{
                    fontWeight: "600",
                    display: "flex ",
                    alignItems: "center",
                    fontSize: ".75rem",
                  }}
                >
                  Transaction Id<span style={{ color: "red" }}>*</span>
                </Typography>

                <TextField
                  disabled
                  value={registrationFee?.txnId}
                  sx={{}}
                  fullWidth
                  size="small"
                  InputProps={{
                    style: {
                      fontSize: ".75rem",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} order={{xs:7}} sx={{}}>
                <Typography
                  sx={{
                    fontWeight: "600",
                    display: "flex ",
                    alignItems: "center",
                    fontSize: ".75rem",
                  }}
                >
                  Amount Paid
                </Typography>
                <TextField
                  disabled
                  value={registrationFee?.totalAmount}
                  fullWidth
                  size="small"
                  InputProps={{
                    style: {
                      fontSize: ".75rem",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} order={{xs:8}} sx={{}}>
                <Typography
                  sx={{
                    fontWeight: "600",
                    display: "flex ",
                    alignItems: "center",
                    fontSize: ".75rem",
                  }}
                >
                  Discount(%)
                </Typography>
                <TextField

                  disabled
                  value={registrationFee?.discountPercent}
                  fullWidth
                  size="small"
                  InputProps={{
                    style: {
                      fontSize: ".75rem",
                    },
                  }}
                />
              </Grid>
            </>
          }
        </Grid>
      </Card>
    </Grid>
  );
}

export default RegistrationFeeSection;
